import { useCallback, useEffect, useRef, useState } from "react";
import { followUpListApi } from "../../redux/apis/follow-up-api";
import { getDueRange, todayDate } from "../../utils/follow-up";
import errorToast from "../helper-components/error-toast";

const LOAD_ERROR = "Please check your connection and try again.";

// The follow-ups for the current filters / page, with the summary counts.
// reload({ silent: true }) refreshes without swapping the list for skeletons.
export const useFollowUpList = ({ filters, search, currentPage, pageSize }) => {
  const [state, setState] = useState({
    followUps: [],
    total: 0,
    summary: null,
    isLoading: true,
    loadError: "",
  });
  // only the latest request may update the list
  const requestIdRef = useRef(0);

  const reload = useCallback(
    async ({ silent = false } = {}) => {
      const requestId = ++requestIdRef.current;
      if (!silent) {
        setState((previous) => ({ ...previous, isLoading: true, loadError: "" }));
      }
      try {
        const res = await followUpListApi({
          status: filters.status === "all" ? undefined : filters.status,
          ...getDueRange(filters.due, filters),
          search: search || undefined,
          page: currentPage,
          limit: pageSize,
          today: todayDate(),
        });
        if (requestId !== requestIdRef.current) return;
        const result = res?.data?.data ?? {};
        setState({
          followUps: result.data ?? [],
          total: result.total ?? 0,
          summary: result.summary ?? null,
          isLoading: false,
          loadError: "",
        });
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        const message = error?.response?.data?.message ?? LOAD_ERROR;
        if (silent) {
          errorToast(`Couldn't refresh the follow-ups. ${message}`);
          return;
        }
        setState((previous) => ({ ...previous, isLoading: false, loadError: message }));
      }
    },
    [filters, search, currentPage, pageSize]
  );

  useEffect(() => {
    reload();
  }, [reload]);

  // changes: an object, or a function of the follow-up returning one
  const updateFollowUp = useCallback((rfuId, changes) => {
    setState((previous) => ({
      ...previous,
      followUps: previous.followUps.map((followUp) =>
        followUp.rfu_id === rfuId
          ? { ...followUp, ...(typeof changes === "function" ? changes(followUp) : changes) }
          : followUp
      ),
    }));
  }, []);

  return { ...state, reload, updateFollowUp };
};
